import Box from '@mui/material/Box';
import { SxProps, Theme } from '@mui/material';
import { useColors, useColorMode } from '../theme/ColorModeContext';

interface GlowCardProps {
  children: React.ReactNode;
  glowOnHover?: boolean;
  gradientBorder?: boolean;
  accentTop?: boolean;
  animate?: boolean;
  animateDelay?: number;
  sx?: SxProps<Theme>;
  onClick?: () => void;
}

const GlowCard = ({
  children,
  glowOnHover = true,
  gradientBorder = false,
  accentTop = false,
  animate = false,
  animateDelay = 0,
  sx,
  onClick,
}: GlowCardProps) => {
  const colors = useColors();
  const { mode } = useColorMode();
  const isDark = mode === 'dark';

  return (
    <Box
      onClick={onClick}
      sx={{
        background: isDark
          ? `linear-gradient(180deg, rgba(255,255,255,0.055) 0%, rgba(255,255,255,0.025) 100%), ${colors.bg.secondary}`
          : colors.gradient.card,
        backdropFilter: 'blur(18px) saturate(1.35)',
        WebkitBackdropFilter: 'blur(18px) saturate(1.35)',
        border: gradientBorder
          ? `1px solid ${colors.border.accent}`
          : `1px solid ${colors.border.subtle}`,
        borderRadius: '8px',
        boxShadow: isDark
          ? '0 24px 80px rgba(0, 0, 0, 0.28)'
          : '0 18px 60px rgba(0, 0, 0, 0.055)',
        transition: 'transform 0.35s cubic-bezier(0.4, 0, 0.2, 1), box-shadow 0.35s cubic-bezier(0.4, 0, 0.2, 1), border-color 0.35s cubic-bezier(0.4, 0, 0.2, 1), background 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
        overflow: 'hidden',
        position: 'relative',
        '&::after': {
          content: '""',
          position: 'absolute',
          inset: '8px',
          pointerEvents: 'none',
          opacity: gradientBorder ? 0.95 : 0.55,
          background: `
            linear-gradient(${colors.accent.cyan}, ${colors.accent.cyan}) left top / 18px 1px no-repeat,
            linear-gradient(${colors.accent.cyan}, ${colors.accent.cyan}) left top / 1px 18px no-repeat,
            linear-gradient(${colors.accent.cyan}, ${colors.accent.cyan}) right bottom / 18px 1px no-repeat,
            linear-gradient(${colors.accent.cyan}, ${colors.accent.cyan}) right bottom / 1px 18px no-repeat
          `,
        },
        ...(accentTop && {
          borderTop: 'none',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '2px',
            background: `linear-gradient(90deg, ${colors.accent.cyan}, transparent)`,
            zIndex: 1,
          },
        }),
        ...(animate && {
          animation: `fadeInUp 0.5s ease-out ${animateDelay}s both`,
        }),
        ...(glowOnHover && {
          '&:hover': {
            transform: 'translateY(-4px)',
            boxShadow: `0 18px 54px ${colors.accent.cyanGlow}, ${isDark ? '0 24px 80px rgba(0, 0, 0, 0.32)' : '0 22px 70px rgba(0, 0, 0, 0.08)'}`,
            borderColor: colors.border.accent,
          },
        }),
        ...(onClick && { cursor: 'pointer' }),
        ...sx as any,
      }}
    >
      {children}
    </Box>
  );
};

export default GlowCard;
